import React, { useContext, useEffect, useReducer } from "react";
import {
  IGetListOfMerchants,
  IOrderStatus,
  IOrdersFiltersHeader,
  IAvailableMultipleValueFilter,
  IAvailableFilter,
  ISlaAlertLevelValue,
} from "../../interfaces/order";
import { DateTimeValidationError } from "@mui/x-date-pickers/internals/hooks/validation/useDateTimeValidation";
import {
  ERROR,
  GET,
  MARKETPLACE,
  ORDER_STATUS,
  OWN_FLEET_ORDER_STATUSES,
  ORDERS_V2_FILTERS_BLACKLIST,
} from "../../utils/constants";
import config from "../../config/config";
import { toISOOrUndefined, compareDates } from "../../utils/orders";
import { UserContext } from "../../contexts/userContext";
import { fetchData } from "../../utils/dataProvider";
import { IAcceptedIntegration } from "../../interfaces/integrations";
import FiltersHeaderView from "./FiltersHeaderView";
import {
  initialOrdersFiltersUIState,
  ordersFiltersUIReducer,
  TOrdersFiltersUIAction,
} from "../../reducers/ordersFiltersReducer";

const parseMerchantsFromIntegrations = (integrations: IAcceptedIntegration[]) =>
  integrations.map(
    ({
      merchant: {
        id,
        entity: { name },
      },
    }: IAcceptedIntegration) => ({ id, label: name })
  );

const getListOfMerchants = async ({
  setMerchants,
  setAlert,
}: IGetListOfMerchants) => {
  try {
    const { integrations } = await fetchData({
      url: "/marketplaces_merchants/acceptedIntegrationsOfMarketplace",
      method: GET,
    });
    const parsedMerchants = parseMerchantsFromIntegrations(integrations);
    setMerchants(parsedMerchants);
  } catch (error: unknown) {
    setAlert({
      typeOfAlert: ERROR,
      message: String(error),
      showAlert: true,
    });
  }
};

const getAvailableStatuses = (all: IOrderStatus[], allowedIds?: string[]) =>
  Array.isArray(allowedIds) && allowedIds.length > 0
    ? all.filter((status) =>
      allowedIds
        .map((id) => String(id).toLowerCase())
        .includes(String(status.id).toLowerCase())
    )
    : all;

const replaceStatusByAlias = (
  aliases: Record<string, string>,
  setter: (_value: IOrderStatus[]) => void,
  isOwnFleet: boolean,
  allowedIds?: string[]
) => {
  const allStatuses = isOwnFleet ? OWN_FLEET_ORDER_STATUSES : ORDER_STATUS;
  const availableStatuses = getAvailableStatuses(allStatuses, allowedIds);
  setter(
    availableStatuses.map(({ label, id, ...rest }) => ({
      ...rest,
      id,
      label: aliases[id] || label,
      description: rest.description,
      type: rest.type,
    }))
  );
};

const setSelectedStatusProxy = (
  value: string | ((_prev: string) => string),
  selectedStatus: string,
  dispatch: React.Dispatch<TOrdersFiltersUIAction>
) => {
  const next = typeof value === "function" ? value(selectedStatus) : value;
  dispatch({ type: "setSelectedStatus", value: next });
};

const setSelectedStatusesProxy = (
  value: IOrderStatus[] | ((_prev: IOrderStatus[]) => IOrderStatus[]),
  selectedStatuses: IOrderStatus[],
  dispatch: React.Dispatch<TOrdersFiltersUIAction>
) => {
  const next = typeof value === "function" ? value(selectedStatuses) : value;
  dispatch({ type: "setSelectedStatuses", value: next });
};

const getMultipleFilter = (
  id: string,
  availableFilters?: IAvailableFilter[]
): IAvailableMultipleValueFilter | undefined =>
  availableFilters?.find(
    (filter) => filter.id === id && filter.type === "multiple_value_filter"
  ) as IAvailableMultipleValueFilter | undefined;

const hasFilterFor = (
  id: string,
  v2Enabled: boolean,
  availableFilters?: IAvailableFilter[]
): boolean =>
  !v2Enabled ||
  !availableFilters ||
  (availableFilters.some((filter) => filter.id === id) &&
    !ORDERS_V2_FILTERS_BLACKLIST.includes(id));

type Dispatch = React.Dispatch<TOrdersFiltersUIAction>;

const makeHandleChangeDate =
  ({
    dispatch,
    picker,
    errors,
    handleChangeFilter,
    v2Enabled,
  }: {
    dispatch: Dispatch;
    picker: any;
    errors: any;
    handleChangeFilter: IOrdersFiltersHeader["handleChangeFilter"];
    v2Enabled: boolean;
  }) =>
    (dateType: "fromDate" | "toDate", newValue: any) => {
      dispatch({
        type: "setErrors",
        value: { ...errors, invalidFrom: false, invalidTo: false },
      });

      const isDateValid = compareDates(
        dateType === "fromDate" ? newValue : picker.fromDate,
        dateType === "fromDate" ? picker.toDate : newValue
      );

      if (isDateValid) {
        const iso = toISOOrUndefined(String(newValue ?? ""));
        handleChangeFilter(
          dateType,
          v2Enabled ? iso || null : (newValue as string | null | undefined)
        );
      } else {
        dispatch({
          type: "setErrors",
          value: {
            ...errors,
            invalidFrom: dateType === "fromDate",
            invalidTo: dateType === "toDate",
          },
        });
      }

      dispatch({ type: "setPickerField", key: dateType, value: newValue });
    };

const createSearchHandlers = (
  dispatch: Dispatch,
  handleChangeFilter: IOrdersFiltersHeader["handleChangeFilter"],
  searchValue: string
) => ({
  handleSubmit: () => {
    handleChangeFilter("search", searchValue || undefined);
    dispatch({ type: "setActiveSearch", value: true });
  },
  handleChange: (value: string) => {
    dispatch({ type: "setSearchValue", value });
    dispatch({ type: "setActiveSearch", value: false });
  },
  handleClose: () => {
    handleChangeFilter("tracking_id", undefined);
    dispatch({ type: "setSearchValue", value: "" });
    dispatch({ type: "setActiveSearch", value: false });
  },
});

// eslint-disable-next-line max-lines-per-function
const FiltersHeaderContainer = (props: IOrdersFiltersHeader) => {
  const {
    handleChangeFilter,
    setAlert,
    aliases,
    selectedOrdersCount = 0,
    setOpenEditModal,
    disableEdit = false,
    isOwnFleet = false,
    loaderPage,
    allowedStatusIds,
    availableFilters,
  } = props;
  const {
    currentUser: { entity },
  } = useContext(UserContext);
  const [state, dispatch] = useReducer(
    ordersFiltersUIReducer,
    initialOrdersFiltersUIState
  );
  const { statusList, selectedCarriers, selectedIds, selectedSlaAlertLevels } = state;
  const v2Enabled = !!config.ORDERS_V2_ENABLED;
  const {
    selectedStatus,
    selectedStatuses,
    picker,
    searchValue,
    activeSearch,
    merchants,
    selectedMerchantId,
    errors,
  } = state;

  const handleChangeDate = makeHandleChangeDate({
    dispatch,
    picker,
    errors,
    handleChangeFilter,
    v2Enabled,
  });

  const { handleSubmit, handleChange, handleClose } = createSearchHandlers(
    dispatch,
    handleChangeFilter,
    searchValue
  );

  const idFilter = getMultipleFilter("_id", availableFilters);
  const carrierOptions =
    (getMultipleFilter("carrier", availableFilters)?.values || []) as string[];
  const idOptions = (idFilter?.values || []) as string[];
  const slaAlertLevelOptions =
    (getMultipleFilter("sla_alert_level", availableFilters)?.values ||
      []) as (string | ISlaAlertLevelValue)[];
  const showStatusFilter =
    hasFilterFor("status", v2Enabled, availableFilters) ||
    selectedStatuses.length > 0 ||
    (!!selectedStatus && !v2Enabled);
  const showCarrierFilter =
    (hasFilterFor("carrier", v2Enabled, availableFilters) ||
      selectedCarriers.length > 0) &&
    !isOwnFleet;
  const showIdFilter =
    hasFilterFor("_id", v2Enabled, availableFilters) || selectedIds.length > 0;
  const showSlaAlertLevelFilter =
    hasFilterFor("sla_alert_level", v2Enabled, availableFilters) || selectedSlaAlertLevels.length > 0;
  const showMerchantFilter =
    (hasFilterFor("merchant_id", v2Enabled, availableFilters) ||
      selectedMerchantId !== undefined) &&
    entity === MARKETPLACE;
  const showDateFilters = true;

  const handleClearFilters = () => {
    dispatch({ type: "resetAll" });
    replaceStatusByAlias(
      aliases,
      (list: IOrderStatus[]) => dispatch({ type: "setStatusList", value: list }),
      isOwnFleet,
      allowedStatusIds
    );

    const filtersToReset: [string, string | null | undefined][] = [
      ["fromDate", null],
      ["toDate", null],
      ["tracking_id", undefined],
      ["status", undefined],
      ["_id", undefined],
      ["merchantId", undefined],
      ["sla_alert_level", undefined],
    ];
    filtersToReset.forEach(([key, value]) => handleChangeFilter(key, value));
  };

  const viewProps = {
    loaderPage,
    isOwnFleet,
    picker,
    errors,
    showMerchantFilter,
    merchants,
    onMerchantChange: (merchantId?: number) => {
      dispatch({ type: "setSelectedMerchantId", value: merchantId });
      handleChangeFilter("merchantId", merchantId);
    },
    showCarrierFilter,
    carrierOptions,
    selectedCarriers,
    onCarriersChange: (values: string[]) => {
      dispatch({ type: "setSelectedCarriers", value: values });
      handleChangeFilter("carrier", values);
    },
    searchValue,
    activeSearch,
    handleSubmit,
    handleChange,
    handleClose,
    v2Enabled,
    showIdFilter,
    idOptions,
    selectedIds,
    onIdsChange: (values: string[]) => {
      dispatch({ type: "setSelectedIds", value: values });
      handleChangeFilter("_id", values);
    },
    showStatusFilter,
    statusList,
    selectedStatus,
    setSelectedStatus: (value: string | ((_prev: string) => string)) =>
      setSelectedStatusProxy(value, selectedStatus, dispatch),
    selectedStatuses,
    setSelectedStatuses: (
      value: IOrderStatus[] | ((_prev: IOrderStatus[]) => IOrderStatus[])
    ) => setSelectedStatusesProxy(value, selectedStatuses, dispatch),
    handleChangeFilter,
    entity,
    selectedOrdersCount,
    setOpenEditModal,
    disableEdit,
    handleClearFilters,
    showSlaAlertLevelFilter,
    slaAlertLevelOptions,
    selectedSlaAlertLevels,
    onSlaAlertLevelsChange: (values: string[]) => {
      dispatch({ type: "setSelectedSlaAlertLevels", value: values });
      handleChangeFilter("sla_alert_level", values);
    },
    onFromDateChange: (value: unknown) => handleChangeDate("fromDate", value),
    onFromDateError: (reason: DateTimeValidationError) =>
      dispatch({ type: "setErrors", value: { ...errors, from: !!reason } }),
    onToDateChange: (value: unknown) => handleChangeDate("toDate", value),
    onToDateError: (reason: DateTimeValidationError) =>
      dispatch({ type: "setErrors", value: { ...errors, to: !!reason } }),
    showDateFilters,
  };

  useEffect(() => {
    if (!v2Enabled) dispatch({ type: "setSelectedStatus", value: "" });
    replaceStatusByAlias(
      aliases,
      (list: IOrderStatus[]) =>
        dispatch({ type: "setStatusList", value: list }),
      isOwnFleet,
      allowedStatusIds
    );
  }, [availableFilters, aliases, isOwnFleet, allowedStatusIds, v2Enabled]);

  useEffect(() => {
    if (entity === MARKETPLACE)
      getListOfMerchants({
        setMerchants: (merchant) =>
          dispatch({ type: "setMerchants", value: merchant }),
        setAlert,
      });
  }, []);

  return <FiltersHeaderView viewProps={viewProps} />;
};

export default FiltersHeaderContainer;
